import {
  type AnchorHTMLAttributes,
  type ButtonHTMLAttributes,
  type CSSProperties,
  forwardRef,
  type InputHTMLAttributes,
  type ReactNode,
  type SelectHTMLAttributes,
} from 'react';

import { ChevronDownIcon } from './icons';
import styles from './ui.module.css';

const cx = (...parts: unknown[]) =>
  parts
    .filter((p): p is string => typeof p === 'string' && p.length > 0)
    .join(' ');

/** Chakra <Stack spacing=X>: a flex column whose children are `gap` apart. */
export function Stack({
  gap,
  align,
  fullWidth,
  className,
  children,
}: {
  gap: string;
  align?: 'flex-start';
  fullWidth?: boolean;
  className?: string;
  children: ReactNode;
}) {
  return (
    <div
      className={cx(
        styles.stack,
        align === 'flex-start' && styles.alignStart,
        fullWidth && styles.fullWidth,
        className
      )}
      style={{ '--stack-gap': gap } as React.CSSProperties}
    >
      {children}
    </div>
  );
}

export function Field({
  error,
  children,
}: {
  error?: ReactNode;
  children: ReactNode;
}) {
  return (
    <div className={styles.field}>
      {children}
      {error && <div className={styles.errorMessage}>{error}</div>}
    </div>
  );
}

/**
 * `rightSlot` overlays the end of the field rather than shortening it — the
 * field keeps its full width and the icon sits on top, which is how Chakra's
 * InputGroup/InputRightElement pair behaved.
 */
export const TextInput = forwardRef<
  HTMLInputElement,
  InputHTMLAttributes<HTMLInputElement> & {
    invalid?: boolean;
    rightSlot?: ReactNode;
    /** Overrides the slot's trailing offset (`--ap-right-slot-mr`). */
    rightSlotStyle?: CSSProperties;
  }
>(function TextInput(
  { invalid, rightSlot, rightSlotStyle, className, ...props },
  ref
) {
  const input = (
    <input
      ref={ref}
      className={cx(
        styles.input,
        rightSlot && styles.inputWithRight,
        className
      )}
      aria-invalid={invalid || undefined}
      {...props}
    />
  );
  if (!rightSlot) {
    return input;
  }
  return (
    <div className={styles.inputGroup}>
      {input}
      <div className={styles.rightElement} style={rightSlotStyle}>
        {rightSlot}
      </div>
    </div>
  );
});

export function Button({
  className,
  type = 'button',
  ...props
}: ButtonHTMLAttributes<HTMLButtonElement>) {
  return (
    <button type={type} className={cx(styles.button, className)} {...props} />
  );
}

export function IconButton({
  className,
  type = 'button',
  ...props
}: ButtonHTMLAttributes<HTMLButtonElement>) {
  return (
    <button
      type={type}
      className={cx(styles.iconButton, className)}
      {...props}
    />
  );
}

export function Link({
  className,
  ...props
}: AnchorHTMLAttributes<HTMLAnchorElement>) {
  return <a className={cx(styles.link, className)} {...props} />;
}

/** A link that performs an action rather than navigating. */
export function LinkButton({
  className,
  type = 'button',
  ...props
}: ButtonHTMLAttributes<HTMLButtonElement>) {
  return (
    <button
      type={type}
      className={cx(styles.linkButton, className)}
      {...props}
    />
  );
}

export function Spinner({
  size = 'md',
  className,
}: {
  size?: 'xs' | 'md';
  className?: string;
}) {
  return (
    <span
      className={cx(
        styles.spinner,
        size === 'xs' && styles.spinnerXs,
        className
      )}
      role="status"
      aria-label="Loading"
    />
  );
}

/**
 * Styled native <select>: keeps the platform keyboard behaviour and mobile
 * picker, with the chevron drawn as a non-interactive overlay so clicks fall
 * through to the control.
 */
export function Select({
  placeholder,
  className,
  children,
  ...props
}: SelectHTMLAttributes<HTMLSelectElement> & { placeholder?: string }) {
  return (
    <div className={styles.selectWrap}>
      <select
        className={cx(
          styles.select,
          props.value && styles.selectFilled,
          className
        )}
        {...props}
      >
        {placeholder !== undefined && <option value="">{placeholder}</option>}
        {children}
      </select>
      <span className={styles.selectIcon} aria-hidden="true">
        <ChevronDownIcon />
      </span>
    </div>
  );
}

export { styles as uiStyles };
